/* eslint-disable no-unused-vars */
import { get, groupBy, isEmpty, meanBy, sumBy } from 'lodash';
import dayjs from 'utils/dayjs';

const filterData = (data, filters) => {
    let filteredData = [];
    if (!isEmpty(filters)) {
        const dateFilters = groupBy(
            filters.filter((f) => f.dateFormat),
            'dateFormat',
        );
        filteredData = data.filter((d) =>
            Object.entries(dateFilters)
                .map(([_key, value]) => value.some((f) => dayjs(d[f.property]).format(f.dateFormat) === f.equals))
                .every((f) => f === true),
        );
        const nonDateFilters = groupBy(
            filters.filter((f) => !f.dateFormat),
            'property',
        );
        filteredData = filteredData.filter((d) =>
            Object.entries(nonDateFilters)
                .map(([_key, value]) => value.some((f) => d[f.property] === f.equals))
                .every((f) => f === true),
        );
    } else {
        filteredData = data;
    }
    return filteredData;
};

const getReservationStatistics = (reservations, filters) => {
    if (isEmpty(reservations)) {
        return null;
    }
    const resData = filterData(reservations, filters);
    return {
        rooms: sumBy(resData, 'rooms') || 0,
        revenue: {
            accomodation: {
                net: sumBy(resData, 'revAccomodationAmountNet') || 0,
                gross: sumBy(resData, 'revAccomodationAmountGross') || 0,
            },
            foodAndBeverage: {
                net: sumBy(resData, 'revFBAmountNet') || 0,
                gross: sumBy(resData, 'revFBAmountGross') || 0,
            },
            other: {
                net: sumBy(resData, 'revOthersAmountNet') || 0,
                gross: sumBy(resData, 'revOthersAmountGross') || 0,
            },
        },
        reservations: resData,
    };
};

const constructGroupingBreakdown = (reservations, filters) =>
    Object.keys(reservations).map((key) => {
        const reservationsForBreakdown = reservations[key] || [];
        return {
            name: key,
            stats: getReservationStatistics(reservationsForBreakdown, filters),
        };
    });

const constructBreakdown = (data, filters) => {
    const breakdown = {};
    ['guaranteed', 'unguaranteed', 'tentative', 'cancelled'].forEach((key) => {
        const { reservations, ...otherData } = get(data, `[${key}]`, { reservations: {} });
        if (!isEmpty(reservations)) {
            const resStats = getReservationStatistics(reservations, filters);
            if (!isEmpty(resStats)) {
                breakdown[key] = {
                    ...otherData,
                    ...resStats,
                    roomTypes: constructGroupingBreakdown(groupBy(reservations, 'roomTypeLookupHandle'), filters),
                    segments: constructGroupingBreakdown(groupBy(reservations, 'segmentsMarketSegment'), filters),
                    segmentBuckets: constructGroupingBreakdown(groupBy(reservations, 'bucketName'), filters),
                    channels: constructGroupingBreakdown(groupBy(reservations, 'segmentsChannel'), filters),
                    sources: constructGroupingBreakdown(groupBy(reservations, 'segmentsSourceName'), filters),
                    rates: constructGroupingBreakdown(groupBy(reservations, 'rateProfilePackageReference'), filters),
                    lengthOfStays: constructGroupingBreakdown(groupBy(reservations, 'lengthOfStay'), filters),
                };
            }
        }
    });
    if (isEmpty(breakdown)) {
        return data;
    }
    breakdown.adr = data.adr;
    breakdown.revPar = data.revPar;
    breakdown.occupancy = data.occupancy;
    return breakdown;
};

const updateObjectWithStats = (data, filters, lyCalculation = 'year') =>
    data.map((d) => {
        const { current, lyFinal, lySpit, ...otherData } = { ...d };
        const { stats: currentStats, ...otherCurrentData } = { ...current };
        const { stats: lyFinalStats, ...otherLyFinalData } = { ...lyFinal };
        const { stats: lySpitStats, ...otherLySpitData } = { ...lySpit };

        return {
            ...otherData,
            lyStayDate:
                lyCalculation === 'week'
                    ? dayjs(d.stayDate).subtract(52, 'week').format('YYYY-MM-DD')
                    : dayjs(d.stayDate).subtract(1, 'year').format('YYYY-MM-DD'),
            current: {
                ...otherCurrentData,
                stats: constructBreakdown(currentStats, filters),
            },
            lyFinal: {
                ...otherLyFinalData,
                stats: constructBreakdown(lyFinalStats, filters),
            },
            lySpit: {
                ...otherLySpitData,
                stats: constructBreakdown(lySpitStats, filters),
            },
        };
    });

const calculateHeaderStats = (data, filters, headerStats) => {
    let allFilteredData = [];
    const lyCalculation = filters.find((f) => f.grouping === 'ddd') ? 'week' : 'year';
    const filteredData = updateObjectWithStats(
        data,
        filters.filter((f) => f.filterResData),
        lyCalculation,
    );
    if (!isEmpty(filters.filter((f) => !f.filterResData))) {
        allFilteredData = filterData(
            filteredData,
            filters.filter((f) => !f.filterResData),
        );
    } else {
        allFilteredData = filteredData;
    }

    const roomsSold = sumBy(allFilteredData, 'current.stats.guaranteed.rooms');
    const revenue = sumBy(allFilteredData, 'current.stats.guaranteed.revenue.accomodation.net');
    const availableRooms = sumBy(allFilteredData, 'current.wholeHouseInv.availableRoomCount');
    const forecastRevenue = sumBy(allFilteredData, 'forecast.revenue');
    const budgetRevenue = sumBy(allFilteredData, 'budget.revenue.accommodation');
    const lyspitRevenue = sumBy(allFilteredData, 'lySpit.stats.guaranteed.revenue.accomodation.net');
    const adr = roomsSold > 0 ? revenue / roomsSold : 0;
    const revpar = availableRooms > 0 ? revenue / availableRooms : 0;
    const occupancy = availableRooms > 0 ? roomsSold / availableRooms : 0;
    const forecastRevenueDiff = revenue - forecastRevenue;
    const budgetRevenueDiff = revenue - budgetRevenue;
    const lyspitRevenueDiff = revenue - lyspitRevenue;
    const lySpit = {
        roomsSold: sumBy(allFilteredData, 'lySpit.stats.guaranteed.rooms'),
        revenue: sumBy(allFilteredData, 'lySpit.stats.guaranteed.revenue.accomodation.net'),
        adr: meanBy(allFilteredData, 'lySpit.stats.adr.net'),
        revPar: meanBy(allFilteredData, 'lySpit.stats.revPar.net'),
        occupancy: meanBy(allFilteredData, 'lySpit.stats.occupancy'),
    };
    const lyFinal = {
        roomsSold: sumBy(allFilteredData, 'lyFinal.stats.guaranteed.rooms'),
        revenue: sumBy(allFilteredData, 'lyFinal.stats.guaranteed.revenue.accomodation.net'),
        adr: meanBy(allFilteredData, 'lyFinal.stats.adr.net'),
        revPar: meanBy(allFilteredData, 'lyFinal.stats.revPar.net'),
        occupancy: meanBy(allFilteredData, 'lyFinal.stats.occupancy'),
    };
    const budget = {
        roomsSold: sumBy(allFilteredData, 'budget.rooms'),
        revenue: sumBy(allFilteredData, 'budget.revenue.accommodation'),
        adr: meanBy(allFilteredData, 'budget.adr'),
        revPar: meanBy(allFilteredData, 'budget.revPar'),
        occupancy: meanBy(allFilteredData, 'budget.occupancy'),
    };
    const forecast = {
        roomsSold: sumBy(allFilteredData, 'forecast.rooms'),
        revenue: sumBy(allFilteredData, 'forecast.revenue'),
        adr: meanBy(allFilteredData, 'forecast.adr.net'),
        revPar: meanBy(allFilteredData, 'forecast.revPar.net'),
        occupancy: meanBy(allFilteredData, 'forecast.occupancy'),
    };
    return {
        ...headerStats,
        roomsSold,
        revenue,
        adr,
        revpar,
        occupancy,
        forecastRevenueDiff,
        budgetRevenueDiff,
        lyspitRevenueDiff,
        lySpit,
        lyFinal,
        budget,
        forecast,
    };
};

export { updateObjectWithStats, calculateHeaderStats, filterData };
